// Lop duy nhat noi ra backend. Moi loi HTTP deu duoc doi thanh ApiError co
// thong diep doc duoc, de man hinh khong phai tu boc tach "detail".

import type {
  DataFile,
  Dataset,
  Health,
  KeyCheck,
  LogLine,
  ProductDetail,
  ProductList,
  Provider,
  RunMeta,
  Step,
  CaiDat,
  KeHoachDon,
  ThongKeKho,
  NguoiDung,
  PhienPanel,
  ChoPhep,
} from "./types";

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

/** Boc tach thong diep loi tu nhieu dang "detail" ma FastAPI co the tra ve. */
function docLoi(status: number, body: unknown): ApiError {
  const d = (body as { detail?: unknown })?.detail;
  if (typeof d === "string") return new ApiError(status, d, body);
  if (d && typeof d === "object") {
    const o = d as { message?: string; loi?: string[] };
    const phu = o.loi?.length ? " " + o.loi.join("; ") : "";
    if (o.message) return new ApiError(status, o.message + phu, d);
  }
  if (Array.isArray(d) && d.length) {
    // Loi kiem tra kieu cua pydantic
    const first = d[0] as { loc?: unknown[]; msg?: string };
    return new ApiError(status, `${first.loc?.join(".") ?? ""} ${first.msg ?? ""}`.trim(), d);
  }
  return new ApiError(status, `Backend tra ve HTTP ${status}.`, body);
}

// Cookie phien het han -> moi loi goi deu 401. Bao cho tang dang nhap biet
// mot lan, thay vi de tung man hinh tu bao loi kieu khac nhau.
let xuLy401: (() => void) | null = null;

export function dangKyXuLy401(f: () => void) {
  xuLy401 = f;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch("/api" + path, {
      ...init,
      headers: {
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...init?.headers,
      },
    });
  } catch (e) {
    throw new ApiError(0, "Không gọi được backend. Kiểm tra backend đã chạy chưa.", e);
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (res.status === 401 && !path.startsWith("/dang-nhap")) xuLy401?.();
  if (!res.ok) throw docLoi(res.status, body);
  return body as T;
}

const json = (data: unknown) => ({ body: JSON.stringify(data) });

export const api = {
  health: () => req<Health>("/health"),

  // --- dang nhap ---
  toi: () => req<{ nguoi_dung: NguoiDung; co_phien_panel: boolean }>("/toi"),

  dangNhap: (tai_khoan: string, mat_khau: string) =>
    req<{ nguoi_dung: NguoiDung }>("/dang-nhap", {
      method: "POST",
      ...json({ tai_khoan, mat_khau }),
    }),

  dangXuat: () => req<{ ok: boolean }>("/dang-xuat", { method: "POST" }),

  choPhep: () =>
    req<{ cho_phep: ChoPhep; duoc_sua: boolean; khong_go_duoc: string[] }>(
      "/cho-phep",
    ),

  luuChoPhep: (cp: ChoPhep) =>
    req<{ cho_phep: ChoPhep }>("/cho-phep", { method: "PUT", ...json(cp) }),

  dsNguoiDung: () =>
    req<{ nguoi_dung: NguoiDung[]; vnc_san_sang: boolean }>("/nguoi-dung"),

  // --- phien panel co tick captcha ---
  moPhienPanel: (tai_khoan: string, mat_khau: string) =>
    req<{ phien: PhienPanel }>("/phien-panel/mo", {
      method: "POST",
      ...json({ tai_khoan, mat_khau }),
    }),

  xemPhienPanel: (id: string) => req<{ phien: PhienPanel }>(`/phien-panel/${id}`),

  nhanTheSauPhien: (id: string) =>
    req<{ nguoi_dung: NguoiDung }>(`/phien-panel/${id}/xong`, { method: "POST" }),

  huyPhienPanel: (id: string) =>
    req<{ ok: boolean }>(`/phien-panel/${id}/huy`, { method: "POST" }),

  steps: () =>
    req<{
      steps: Step[];
      confirm_phrase: string;
      folders: string[];
      chuoi_mac_dinh: string[];
    }>("/pipeline/steps"),

  files: (folder: string) =>
    req<{ folder: string; files: DataFile[] }>(`/files/${folder}`),

  // --- chay pipeline ---
  runStep: (stepId: string, params: Record<string, unknown>, confirm = "") =>
    req<{ run: RunMeta }>(`/steps/${stepId}/run`, {
      method: "POST",
      ...json({ params, confirm }),
    }),

  runChuoi: (
    steps: string[],
    params: Record<string, Record<string, unknown>>,
    confirm = "",
  ) =>
    req<{ run: RunMeta }>("/chay-chuoi", {
      method: "POST",
      ...json({ steps, params, confirm }),
    }),

  runs: (limit = 50) =>
    req<{ runs: RunMeta[]; dang_chay: RunMeta | null }>(`/runs?limit=${limit}`),

  run: (id: string) => req<{ run: RunMeta; log: LogLine[] }>(`/runs/${id}`),

  cancelRun: (id: string) => req<{ da_dung: string }>(`/runs/${id}/cancel`, { method: "POST" }),

  // --- api key ---
  keys: () => req<{ providers: Record<string, Provider> }>("/keys"),

  thuKey: (provider: string, key: string) =>
    req<KeyCheck>(`/keys/${provider}/thu`, { method: "POST", ...json({ key }) }),

  themKey: (provider: string, key: string, label: string, kiemTraTruoc = true) =>
    req<{ key: unknown; kiem_tra: KeyCheck | null }>(`/keys/${provider}`, {
      method: "POST",
      ...json({ key, label, kiem_tra_truoc: kiemTraTruoc }),
    }),

  kiemTraKeyDaLuu: (provider: string, id: string) =>
    req<{ kiem_tra: KeyCheck }>(`/keys/${provider}/${id}/kiem-tra`, { method: "POST" }),

  batTatKey: (provider: string, id: string, enabled: boolean) =>
    req<unknown>(`/keys/${provider}/${id}`, { method: "PATCH", ...json({ enabled }) }),

  goKey: (provider: string, id: string) =>
    req<unknown>(`/keys/${provider}/${id}`, { method: "DELETE" }),

  // --- san pham ---
  datasets: () => req<{ datasets: Dataset[] }>("/datasets"),

  products: (folder: string, file: string) =>
    req<ProductList>(`/products?folder=${folder}&file=${encodeURIComponent(file)}`),

  product: (folder: string, file: string, stt: number | string) =>
    req<ProductDetail>(
      `/products/${stt}?folder=${folder}&file=${encodeURIComponent(file)}`,
    ),

  // --- cai dat + don dep ---
  caiDat: () =>
    req<{ cai_dat: CaiDat; mac_dinh: CaiDat; thong_ke: ThongKeKho }>("/cai-dat"),

  luuCaiDat: (cd: CaiDat) =>
    req<{ cai_dat: CaiDat; thong_ke: ThongKeKho }>("/cai-dat", {
      method: "PUT",
      ...json(cd),
    }),

  xemTruocDon: (gioiHan?: number) =>
    req<KeHoachDon>(
      "/don-dep/xem-truoc" + (gioiHan ? `?gioi_han=${gioiHan}` : ""),
    ),

  donNgay: (gioiHan?: number) =>
    req<KeHoachDon>("/don-dep" + (gioiHan ? `?gioi_han=${gioiHan}` : ""), {
      method: "POST",
    }),
};

/** Dia chi anh: anh ngoai di qua proxy cua backend de khong bi CDN chan hotlink. */
export function anhUrl(url: string | null | undefined, fileLocal?: string | null): string | null {
  if (fileLocal) return `/api/anh-local/${encodeURIComponent(fileLocal)}`;
  if (!url) return null;
  if (!/^https?:\/\//.test(url)) return null;
  return `/api/anh-proxy?url=${encodeURIComponent(url)}`;
}

/** Mo WebSocket nghe log cua mot lan chay. */
export function moStreamLog(runId: string): WebSocket {
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  return new WebSocket(`${scheme}://${location.host}/api/ws/runs/${runId}`);
}
